import * as React from 'react';
import { oc } from 'ts-optchain';
import _ from 'lodash';
import clsx from 'clsx';

import * as Service from 'types/service';
import { CalendarItem } from 'utils/calendarItems';
import { DisplayTime } from '@tock/time';
import {
  EventAction,
  EventAttributesActionLocation,
} from 'utils/analytics/amplitudeEventConstants';
import { IWithContextProps, core, withContext } from '@tock/coretypes';
import { IconBook, IconEdit, IconGrab, IconPin, IconWalkIn } from '@tock/icons';
import { LocalDate } from 'js-joda';
import { Props as PopoverProps } from 'components/Popover';
import { PrintChitSource } from 'constants/PrinterConstants';
import { ServicePartyDragMode } from 'state/service/ui';
import { ShowAvailabilityOptions } from 'routes/authenticated/app/bookings/CalendarAndServiceRoute';
import { SquareCoordinates } from 'actions/CalendarActions';
import {
  TableIdMap,
  activeCalendarFloorplanTablesByIdSelector,
} from 'selectors/routes/calendar/calendarFloorplanSelector';
import { WalkInTypes } from 'constants/service/walkInConstants';
import { WithAsyncRenderProps, withAsyncRender } from 'contexts/asyncRender';
import { amplitudeSendEvent } from 'utils/analytics/amplitude';
import { calendarSelectedCalendarItemSelector } from 'selectors/calendarSelectors';
import {
  canMarkPartyAsLeft,
  displayStatus,
  generateStateSelectorStatus,
  getButtonActions,
  isPartyWalkIn,
} from 'utils/partyUtils';
import { defaultMemoize } from 'reselect';
import { flowExecutionIdSelector } from 'selectors/flowTrackSelector';
import { getShowAvailabilityPanelParams } from 'utils/CalendarRouteUtils';
import {
  newAssigningSessionSelector,
  servicePartyDragModeSelector,
} from 'selectors/service/serviceUiSelector';
import { openWalkinSidePanel } from 'utils/service/walkInUtils';
import { secondaryStatesSelector } from 'selectors/service/secondaryStatesSelector';
import { selectedPartySelector } from 'selectors/service/selectedPartySelector';
import { serviceDateSelector } from 'selectors/service/serviceAppSelector';
import { ticketTypesForTableIdSelector } from 'selectors/ticketTypesByTableIdSelector';
import { todayBusinessDay } from '@tock/shared/utils/time';
import { trackPartyEventUtility } from 'components/service/party/PartyPopoverFC';
import { v4 as uuidv4 } from 'uuid';
import BaseComponent from '@tock/shared/components/BaseComponent';
import ButtonGroup from '@tock/shared/components/buttons/ButtonGroup';
import CancellablePopover from 'components/CancellablePopover';
import HandSellType from 'constants/HandSellType';
import IconButton from '@tock/shared/components/buttons/IconButton';
import PartyMoveButton from 'components/service/party/PartyMoveButton';
import PartyPanelStateSelector from 'components/service/party-panel/PartyPanelStateSelector';
import PartyStates from 'constants/partyStateConstants';
import TableStateForm from 'components/service/forms/TableStateForm';
import TableStates from 'constants/service/tableStateConstants';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';

interface Props {
  isOpen: boolean;
  isReadOnly: boolean;
  onClose(): void;
  onPrintChit(party: Service.Party | undefined, source: PrintChitSource): void;
  onShowAvailabilityPanel(options?: ShowAvailabilityOptions): void;
  onSelectParty(party: Service.Party): void;
  onTableClicked(tappedTableId: number): void;
  selectedParty?: Service.Party;
  party?: Service.Party;
  nextParty?: Service.Party;
  tableId: number;
  tableState: string;
  tableNode: React.RefObject<HTMLDivElement>;
}

interface ConnectProps extends DefaultConnectProps {
  dragMode: ServicePartyDragMode;
  flowExecutionId: string;
  secondaryStates: Service.StateGroupingMap;
  printOnSeat: boolean;
  newAssigningSession: boolean;
  serviceDate?: string;
  tablesById: TableIdMap;
  ticketTypeIdsForTableId: number[];
  calendarItem?: CalendarItem;
}

type AllProps = Props & ConnectProps & IWithContextProps & WithAsyncRenderProps;

class ServiceAerialTablePopover extends BaseComponent<AllProps> {
  handleSave(party: Service.Party, state: string, secondaryState: string | null) {
    const { actions, onClose } = this.props;
    actions.elmParties.updatePartyState(party, state, secondaryState);
    onClose();
  }

  onUpdatePartyState(party: Service.Party, state: string, secondaryState: string | null): void {
    this.props.actions.elmParties.updatePartyState(party, state, secondaryState);
    this.props.onClose();
  }

  handleSelectParty(party: Service.Party) {
    if (!this.props.selectedParty || this.props.selectedParty.id !== party.id) {
      this.props.onSelectParty(party);
    }
  }

  handleOpenWalkInPanel(): void {
    const { actions } = this.props;
    const newFlowExecutionId = uuidv4();
    this.props.onTableClicked(this.props.tableId);
    actions.flowTrack.setFlowExecutionId(newFlowExecutionId);
    actions.serviceUiWalkIn.openWalkInPanel(WalkInTypes.QUOTED);
    openWalkinSidePanel(actions, newFlowExecutionId, 'NEW', 'service', 'Table context menu');
    amplitudeSendEvent(EventAction.WALK_IN_SEATING_STARTED, {
      actionLocation: EventAttributesActionLocation.TABLE_PANEL,
      flowExecutionId: newFlowExecutionId,
    });
    this.props.onClose();
  }

  handleEditWalkIn(party: Service.Party): void {
    const {
      actions,
      context: { isFeatureEnabled },
    } = this.props;
    const newFlowExecutionId = uuidv4();
    actions.flowTrack.setFlowExecutionId(newFlowExecutionId);
    this.handleSelectParty(party);

    const isQuotedWalkIn = isFeatureEnabled('FLEX_EXPECTED_WALK_INS')
      ? party.quotedTimeMinutes !== undefined
      : !!party.quotedTimeMinutes;

    // once all the panels are in, this will go away and you just open the panel you want.
    if (isQuotedWalkIn) {
      actions.serviceUiWalkIn.openWalkInPanelForEditing({
        walkInType: 'QUOTED',
        walkInParty: party,
      });
      openWalkinSidePanel(actions, newFlowExecutionId, 'EDIT', 'service', 'Table context menu');
    } else {
      actions.serviceUiWalkIn.openWalkInPanelForEditing({
        walkInType: 'EXPECTED',
        walkInParty: party,
      });
      openWalkinSidePanel(actions, newFlowExecutionId, 'EDIT', 'service', 'Table context menu');
    }
    this.props.onClose();
  }

  handleClickViewProfile(party: Service.Party): void {
    const {
      actions: { serviceUiModal, reservationsSidePanel },
    } = this.props;
    this.handleSelectParty(party);
    this.props.actions.reservationsSidePanel.closeSidePanel();
    if (!(isPartyWalkIn(party) && party.walkInType === 'WalkIn')) {
      reservationsSidePanel.openPartyFullPanel();
    } else {
      serviceUiModal.openProfileModal();
    }
  }

  handleBookReservation(): void {
    const {
      actions: { handsell, reservation },
      onShowAvailabilityPanel,
      tableId,
      tablesById,
      ticketTypeIdsForTableId,
      serviceDate,
    } = this.props;

    const selectedTableId = _.toString(tableId);
    const params = getShowAvailabilityPanelParams({
      tablesById,
      selectedTableIds: [selectedTableId],
      date: serviceDate ? LocalDate.parse(serviceDate) : undefined,
      ticketTypeIds: ticketTypeIdsForTableId,
    });

    onShowAvailabilityPanel({ params });
    reservation.openReservation(
      {
        type: 'RESERVATION_SEARCH_TABLE',
        reservationType: HandSellType.CHARGE_NOW,
        reservationAttributes: {
          tock_origin: `book_reservation_button_table_context_menu`,
        },
      },
      EventAttributesActionLocation.TABLE_POPOVER
    );

    handsell.setPurchaseOrderKeyValue({
      tock_origin: `book_reservation_button_table_context_menu`,
    });

    this.props.onClose();
  }

  handleEditParty() {
    const { party } = this.props;
    if (!party) {
      return;
    }
    if (party.type === 'WalkIn' && !oc(party).patronInfo.id()) {
      this.handleEditWalkIn(party);
    } else {
      this.handleClickViewProfile(party);
    }
    this.props.onClose();
  }

  handleEditNextParty() {
    const { nextParty: party } = this.props;
    if (!party) {
      return;
    }
    if (party.type === 'WalkIn' && !oc(party).patronInfo.id()) {
      this.handleEditWalkIn(party);
    } else {
      this.handleClickViewProfile(party);
    }
    this.props.onClose();
  }

  handleUpdateTableState(newState: string): void {
    const {
      actions: { elmTables },
      tableId,
    } = this.props;
    elmTables.updateTableState(tableId, newState);
    this.props.onClose();
  }

  finishParty(party: Service.Party): void {
    const { asyncRender } = this.props;
    if (!party) {
      return;
    }

    if (party.state === PartyStates.left) {
      return;
    }

    if (!canMarkPartyAsLeft(party)) {
      asyncRender
        .confirm(
          `You haven’t collected payment from this party yet. Are you sure you’d like to mark them as finished?`
        )
        .then(() => this.onUpdatePartyState(party, PartyStates.left, null))
        .catch(_.noop);
      return;
    }
    this.onUpdatePartyState(party, PartyStates.left, null);
    this.props.onClose();
  }

  seatParty(party: Service.Party): void {
    const { onPrintChit, printOnSeat } = this.props;
    if (!party) {
      return;
    }
    if (printOnSeat && onPrintChit) {
      onPrintChit(party, PrintChitSource.AUTOMATIC);
    }
    this.onUpdatePartyState(party, PartyStates.seated, null);
    this.props.onClose();
  }

  assignTableAndToggleGuest(): void {
    const { party, newAssigningSession } = this.props;
    if (!party) {
      return;
    }

    if (!newAssigningSession) {
      this.props.actions.serviceUiPartyDisplay.toggleNewAssigningSession();
    }
    this.props.actions.serviceUiPartyDisplay.toggleIsAssigningTables();
    this.handleSelectParty(party);
    this.props.onClose();

    this.trackPartyEvent(EventAction.PARTY_TABLE_ASSIGNMENT_STARTED);
  }

  moveNextParty(): void {
    const { nextParty: party, newAssigningSession } = this.props;
    if (!party) {
      return;
    }

    if (!newAssigningSession) {
      this.props.actions.serviceUiPartyDisplay.toggleNewAssigningSession();
    }
    this.props.actions.serviceUiPartyDisplay.toggleIsAssigningTables();
    this.handleSelectParty(party);
    this.props.onClose();

    this.trackPartyEvent(EventAction.PARTY_TABLE_ASSIGNMENT_STARTED);
  }

  togglePinNextParty(): void {
    const { asyncRender, nextParty: party } = this.props;
    if (!party) {
      return;
    }

    const { visit } = party;
    const togglePin = () =>
      this.props.actions.booking.updatePurchaseVisit(party.id, {
        ...(visit as core.Visit),
        pinnedToTable: !visit.pinnedToTable,
      });
    if (!visit.pinnedToTable) {
      togglePin();
    } else {
      asyncRender
        .confirm('Do you want to unpin this reservation?', {
          cancelText: 'No, keep pinned',
          continueText: 'Yes, unpin',
          isDestructive: true,
        })
        .then(() => {
          togglePin();
        })
        .catch(_.noop);
    }
  }

  trackPartyEvent(action: EventAction) {
    const { party } = this.props;
    if (party) {
      trackPartyEventUtility(action, party);
    }
  }

  getPopoverAndPartyPanelPosition = defaultMemoize(
    (
      props: Props
    ): Partial<PopoverProps> & {
      partyPanelPosition: 'bottom' | 'top';
    } => {
      const coordinates = { openPositionX: 0, openPositionY: 0 };
      if (props.tableNode.current) {
        const rect: DOMRect = props.tableNode.current.getBoundingClientRect() as DOMRect;
        coordinates.openPositionX = rect.x;
        coordinates.openPositionY = rect.y;
      }

      let align: PopoverProps['align'] = 'start';
      if (this.opensToLeft(coordinates)) {
        align = 'before';
      }

      let justify: PopoverProps['justify'] = 'end';
      let partyPanelPosition: 'bottom' | 'top' = 'bottom';
      if (this.opensToTop(coordinates)) {
        justify = 'start';
        partyPanelPosition = 'top';
      }

      return {
        align,
        position: 'vertical',
        justify,
        partyPanelPosition,
      };
    }
  );

  opensToTop(coordinates?: SquareCoordinates): boolean {
    if (!coordinates) {
      return false;
    }
    return document.documentElement.clientHeight - coordinates.openPositionY < 330;
  }

  opensToLeft(coordinates?: SquareCoordinates): boolean {
    if (!coordinates) {
      return false;
    }
    return document.documentElement.clientWidth - coordinates.openPositionX < 240;
  }

  render() {
    const {
      context: { isFlexBusiness },
      dragMode,
      isOpen,
      onClose,
      party,
      nextParty,
      secondaryStates,
      printOnSeat,
      onPrintChit,
      tableState,
      isReadOnly,
      serviceDate,
    } = this.props;

    const {
      buttonText,
      buttonAction,
      buttonIcon,
      buttonColorClassname,
      buttonBorderClassname,
      isDisabled,
    } = getButtonActions(party, secondaryStates, this.seatParty, this.finishParty);
    const {
      buttonText: nextPartyButtonText,
      buttonAction: nextPartyButtonAction,
      buttonIcon: nextPartyButtonIcon,
      buttonColorClassname: nextPartyButtonColorClassname,
      buttonBorderClassname: nextPartyButtonBorderClassname,
      isDisabled: isNextPartyButtonDisabled,
    } = getButtonActions(nextParty, secondaryStates, this.seatParty, this.finishParty);

    const { partyPanelPosition, ...popoverPositionProps } = this.getPopoverAndPartyPanelPosition(
      this.props
    );

    const isToday = serviceDate === todayBusinessDay().toString();

    return (
      <CancellablePopover
        className="ServiceAerialTable-popover"
        align={'center'}
        {...popoverPositionProps}
        isOpen={isOpen}
        onClose={onClose}
      >
        {party && (
          <>
            <div className="ServiceAerialTable-popoverParty">
              <div
                className="PartyBodyCondensed-title ServiceAerialTable-popoverPartyName"
                onClick={this.handleEditParty}
              >
                <span className="PartyBodyCondensed-name">
                  {party.lastNameFirstName}
                  <div className="PartyBodyCondensed-edit">
                    <IconEdit />
                  </div>
                </span>
                <span className="PartyBodyCondensed-size">{party.partySize}</span>
              </div>
              {party.expectedReservationTime && (
                <div className="PartyBodyCondensed-description ServiceAerialTable-popoverDescription">
                  <span className="PartyBodyCondensed-time">
                    <DisplayTime timeValue={party.expectedReservationTime} />
                  </span>
                  <span className="PartyBodyCondensed-status">{displayStatus(party)}</span>
                </div>
              )}
              <PartyPanelStateSelector
                isDisabled={isReadOnly}
                party={party}
                status={generateStateSelectorStatus(party)}
                secondaryStates={secondaryStates}
                handleUpdatePartyState={this.handleSave}
                position={partyPanelPosition}
                onPrintChit={onPrintChit}
                printOnSeat={printOnSeat}
              />

              <ButtonGroup className="ServiceAerialTable-popoverButtons popoverButtons--top">
                <PartyMoveButton
                  IconComponent={IconGrab}
                  dragMode={dragMode}
                  isReadOnly={isReadOnly}
                  stretch
                  variant="primary"
                  party={party}
                  dragSource="TABLE_MENU"
                  onClick={this.assignTableAndToggleGuest}
                  onSuccessfulDrop={this.props.onClose}
                />
              </ButtonGroup>

              {buttonText && (
                <ButtonGroup className="ServiceAerialTable-popoverButtons popoverButtons--mid">
                  <IconButton
                    IconComponent={buttonIcon}
                    disabled={isReadOnly || isDisabled}
                    text={buttonText}
                    stretch
                    modifierClassName={clsx(buttonColorClassname, buttonBorderClassname)}
                    variant="primary"
                    onClick={buttonAction}
                  />
                </ButtonGroup>
              )}
            </div>
          </>
        )}

        {nextParty && (
          <>
            <div className="ServiceAerialTable-popoverParty">
              <div
                className="PartyBodyCondensed-title ServiceAerialTable-popoverPartyName"
                onClick={this.handleEditNextParty}
              >
                <span className="PartyBodyCondensed-name">
                  {nextParty.lastNameFirstName}
                  <div className="PartyBodyCondensed-edit">
                    <IconEdit />
                  </div>
                </span>
                <span className="PartyBodyCondensed-size">{nextParty.partySize}</span>
              </div>
              {nextParty.expectedReservationTime && (
                <div className="PartyBodyCondensed-description ServiceAerialTable-popoverDescription">
                  <span className="PartyBodyCondensed-time">
                    <DisplayTime timeValue={nextParty.expectedReservationTime} />
                  </span>
                  <span className="PartyBodyCondensed-status">{displayStatus(nextParty)}</span>
                </div>
              )}
              <ButtonGroup className="ServiceAerialTable-popoverButtons popoverButtons--top">
                {isFlexBusiness() && nextParty.type === 'Booking' && (
                  <>
                    <IconButton
                      disabled={isReadOnly}
                      className={
                        oc(nextParty).visit.pinnedToTable() ? 'ServiceAerialTable-pinned' : ''
                      }
                      IconComponent={IconPin}
                      text={oc(nextParty).visit.pinnedToTable() ? 'Unpin' : 'Pin'}
                      hideText
                      variant="secondary"
                      onClick={this.togglePinNextParty}
                    />
                    <br />
                  </>
                )}
                <PartyMoveButton
                  IconComponent={IconGrab}
                  dragMode={dragMode}
                  variant="primary"
                  party={nextParty}
                  stretch
                  isReadOnly={isReadOnly}
                  dragSource="TABLE_MENU"
                  onClick={this.moveNextParty}
                />
              </ButtonGroup>
              <ButtonGroup className="ServiceAerialTable-popoverButtons popoverButtons--mid">
                {nextPartyButtonText && (
                  <IconButton
                    IconComponent={nextPartyButtonIcon}
                    disabled={isReadOnly || isNextPartyButtonDisabled}
                    text={nextPartyButtonText}
                    stretch
                    modifierClassName={clsx(
                      nextPartyButtonColorClassname,
                      nextPartyButtonBorderClassname
                    )}
                    variant="primary"
                    onClick={nextPartyButtonAction}
                  />
                )}
              </ButtonGroup>
            </div>
          </>
        )}

        {!party && (
          <>
            <ButtonGroup className="ServiceAerialTable-popoverButtons">
              <IconButton
                IconComponent={IconWalkIn}
                text="Walk-in"
                disabled={tableState !== TableStates.ready || isReadOnly || !isToday}
                stretch
                variant="primary"
                onClick={this.handleOpenWalkInPanel}
              />

              <IconButton
                IconComponent={IconBook}
                disabled={isReadOnly}
                text="Book"
                stretch
                variant="primary"
                onClick={this.handleBookReservation}
              />
            </ButtonGroup>

            <div className="ServiceAerialTable-tableState">
              <TableStateForm
                state={tableState}
                onChange={isReadOnly ? _.noop : this.handleUpdateTableState}
              />
            </div>
          </>
        )}
      </CancellablePopover>
    );
  }
}

export default connect<Props, ConnectProps>(
  withContext(withAsyncRender(ServiceAerialTablePopover)),
  (state: StoreType, props?: Props) => {
    return {
      dragMode: servicePartyDragModeSelector(state),
      flowExecutionId: flowExecutionIdSelector(state),
      printOnSeat: oc(state.business.data).printChitWhenSeating(false),
      secondaryStates: secondaryStatesSelector(state),
      selectedParty: selectedPartySelector(state),
      calendarItem: calendarSelectedCalendarItemSelector(state),
      serviceDate: serviceDateSelector(state),
      newAssigningSession: newAssigningSessionSelector(state),
      tablesById: activeCalendarFloorplanTablesByIdSelector(state),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ticketTypeIdsForTableId: ticketTypesForTableIdSelector(state, [props!.tableId]),
    };
  }
);
